import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { Button, Input } from '../../../ui';
import { useAuth } from '../../../../stores';
import type { ProfileFormData } from '../../../../types';

const profileSchema = z.object({
  firstName: z
    .string()
    .min(1, 'First name is required')
    .max(50, 'First name must be less than 50 characters'),
  lastInitial: z
    .string()
    .min(1, 'Last initial is required')
    .max(1, 'Only one letter please')
    .regex(/^[A-Za-z]$/, 'Must be a letter'),
  phone: z
    .string()
    .optional()
    .refine(
      (val) => !val || /^[\d\s\-+()]+$/.test(val),
      'Please enter a valid phone number'
    ),
  whatsapp: z
    .string()
    .optional()
    .refine(
      (val) => !val || /^[\d\s\-+()]+$/.test(val),
      'Please enter a valid WhatsApp number'
    ),
  location: z.string().optional(),
});

type ProfileSchemaType = z.infer<typeof profileSchema>;

interface ProfileStepProps {
  initialData: ProfileFormData | null;
  onSubmit: (data: ProfileFormData) => void;
}

const inputVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      delay: i * 0.08,
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  }),
};

// Phone number formatting utilities
function formatPhoneNumber(value: string): string {
  // Remove all non-digit characters except +
  const cleaned = value.replace(/[^\d+]/g, '');

  // If empty, return empty
  if (!cleaned) return '';

  // Handle international numbers (starting with +)
  if (cleaned.startsWith('+')) {
    const digits = cleaned.slice(1);

    // US/Canada (+1)
    if (digits.startsWith('1') && digits.length <= 11) {
      const country = '+1';
      const rest = digits.slice(1);
      if (rest.length === 0) return country;
      if (rest.length <= 3) return `${country} (${rest}`;
      if (rest.length <= 6) return `${country} (${rest.slice(0, 3)}) ${rest.slice(3)}`;
      return `${country} (${rest.slice(0, 3)}) ${rest.slice(3, 6)}-${rest.slice(6, 10)}`;
    }

    // Other international - basic grouping
    if (digits.length <= 3) return `+${digits}`;
    if (digits.length <= 6) return `+${digits.slice(0, 3)} ${digits.slice(3)}`;
    if (digits.length <= 10) return `+${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    return `+${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)} ${digits.slice(10)}`;
  }

  // Assume US format for numbers without +
  const digits = cleaned;
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;

  // If more than 10 digits, treat as international and add +
  return formatPhoneNumber(`+${digits}`);
}

export function ProfileStep({ initialData, onSubmit }: ProfileStepProps) {
  const { user } = useAuth();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ProfileSchemaType>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: initialData?.firstName || '',
      lastInitial: initialData?.lastInitial || '',
      phone: initialData?.phone || '',
      whatsapp: initialData?.whatsapp || '',
      location: initialData?.location?.formatted || '',
    },
  });

  const phoneValue = watch('phone');
  const whatsappValue = watch('whatsapp');

  const handlePhoneChange = (field: 'phone' | 'whatsapp') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setValue(field, formatted);
  };

  const onFormSubmit = (data: ProfileSchemaType) => {
    const profileData: ProfileFormData = {
      firstName: data.firstName,
      lastInitial: data.lastInitial.toUpperCase(),
      phone: data.phone || undefined,
      whatsapp: data.whatsapp || undefined,
      location: data.location
        ? {
            formatted: data.location,
          }
        : undefined,
    };
    onSubmit(profileData);
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      {/* Intro text */}
      <motion.p
        className="text-center text-surface-600 dark:text-surface-400 mb-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
      >
        Just a few details to personalize your experience. We only use your first name and last
        initial for privacy.
      </motion.p>

      {/* Email (read-only from auth) */}
      <motion.div custom={0} variants={inputVariants} initial="hidden" animate="visible">
        <Input
          label="Email"
          type="email"
          value={user?.email || ''}
          disabled
          hint="From your account"
        />
      </motion.div>

      {/* Name fields */}
      <div className="grid grid-cols-3 gap-4">
        <motion.div
          className="col-span-2"
          custom={1}
          variants={inputVariants}
          initial="hidden"
          animate="visible"
        >
          <Input
            label="First Name"
            placeholder="Your first name"
            {...register('firstName')}
            error={errors.firstName?.message}
          />
        </motion.div>
        <motion.div custom={2} variants={inputVariants} initial="hidden" animate="visible">
          <Input
            label="Last Initial"
            placeholder="L"
            maxLength={1}
            {...register('lastInitial')}
            error={errors.lastInitial?.message}
            className="text-center uppercase"
          />
        </motion.div>
      </div>

      {/* Phone numbers */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <motion.div custom={3} variants={inputVariants} initial="hidden" animate="visible">
          <Input
            label="Phone Number"
            type="tel"
            placeholder="+1 (555) 123-4567"
            value={phoneValue || ''}
            onChange={handlePhoneChange('phone')}
            error={errors.phone?.message}
            hint="Optional - for sponsor contact"
          />
        </motion.div>
        <motion.div custom={4} variants={inputVariants} initial="hidden" animate="visible">
          <Input
            label="WhatsApp Number"
            type="tel"
            placeholder="+1 (555) 123-4567"
            value={whatsappValue || ''}
            onChange={handlePhoneChange('whatsapp')}
            error={errors.whatsapp?.message}
            hint="Optional"
          />
        </motion.div>
      </div>

      {/* Location - simplified single field */}
      <motion.div custom={5} variants={inputVariants} initial="hidden" animate="visible">
        <Input
          label="Location"
          placeholder="City, State, Zip or Country (e.g., Austin, TX 78701)"
          {...register('location')}
          hint="Optional - helps find local meetings"
        />
      </motion.div>

      {/* Submit button */}
      <motion.div
        className="pt-4"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Continue'}
        </Button>
      </motion.div>
    </form>
  );
}
